/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { strings } from "../../common/Strings/Strings";
import { InvoiceResponse } from "../../models/invoice/InvoiceResponse";
import { getGeneralError } from "../../util/helperFunctions";
import { InvoiceStatus } from "../../models/invoice/InvoiceStatus";
import InvoiceApi from "../../api/InvoiceApi";
import AlertBox from "../../components/AlertBox";
import { downloadInvoicePdf } from "../../util/FileUtils";
import Modal from "../../components/Modal/Modal";
import { ArrowDownTray } from "../../common/Icons/ArrowDownTray";
import Button from "../../components/Button";
import IconBubble from "../../components/IconBubble";
import { CalendarIcon } from "../../common/Icons/Calendar";
import { clinicPlaceholder } from "../../assets/AssetConfig";
import NumberFormatter from "../../util/NumberFormatter";
import { BankNotes } from "../../common/Icons/BankNotes";
import { getCostAccountingState } from "../Accounting/InvoiceListing/InvoiceListRow";
import { Document } from "../../common/Icons/Document";

interface Props {
  isClinicPage: boolean;
  close(): void;
  open: boolean;
  prescriptionId: string;
}

const PrescriptionBillModal: React.FC<Props> = ({ isClinicPage, open, close, prescriptionId }: Props) => {
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const getInvoice = async () => {
      try {
        const response = await InvoiceApi.getInvoiceOfPrescription(prescriptionId);
        if (isMounted) {
          setInvoice(response.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(await getGeneralError(err));
        }
      }
    };

    setError(null);
    setInvoice(null);

    void getInvoice();

    return () => {
      isMounted = false;
    };
  }, [prescriptionId]);

  return (
    <Modal show={open} handleClose={close}>
      <Modal.Header closeButton title={strings.invoiceDetails}></Modal.Header>
      <Modal.Body>
        {invoice !== null ? (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <IconBubble>
                <Document />
              </IconBubble>
              <div className="font-bold"><Link className="tw-link-dark" to={`/invoices/clinic/${invoice?.id}`} style={{ lineHeight: 0 }}>{invoice?.invoiceNumber || strings.invoice}</Link></div>
            </div>
            <div className="flex items-center space-x-3">
              <IconBubble>
                <img src={clinicPlaceholder} />
              </IconBubble>
              <div className="font-bold">{invoice.clinicName}</div>
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <IconBubble>
                  <CalendarIcon />
                </IconBubble>
                <div className="font-bold">{moment(invoice.creationDate).format("LLL")}</div>
              </div>
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <IconBubble>
                  <BankNotes />
                </IconBubble>
                <div className="font-bold">
                  {NumberFormatter.formatPrice(invoice.totalCost, invoice.currency)} 
                  <span className="pl-2">{getCostAccountingState(invoice?.costAccounting?.status)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
        {error !== null ? <AlertBox message={error} /> : <></>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          {strings.close}
        </Button>
        {invoice ? (
          <>
            <Button
              variant="primary"
              hidden={invoice.status === InvoiceStatus.IN_PROGRESS}
              onClick={() => {
                downloadInvoicePdf(invoice);
              }}
            >
              <ArrowDownTray /><span className="pl-2">{strings.download}</span>
            </Button>
            <Link
              hidden={invoice.status !== InvoiceStatus.IN_PROGRESS || !isClinicPage}
              to={`/invoices/edit/${invoice.id}/finish`}
            >
              {strings.goToInvoices}
            </Link>
          </>
        ) : (
          <></>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PrescriptionBillModal;
